import React, { useContext } from "react";
import { Link } from "react-router-dom";
import { IntlContext } from "context";
import { AdFrequency, AdInterface } from "types/ad";
import Image from "components/File/Image";
import moment from "moment";

import { MapPin, RefreshCw, Timer } from "lucide-react";
import { buildRoute } from "../../../routes";
import { formatAdServiceLocation } from "helpers/formatAdServiceLocation";
import { Card, CardTitle, CardDescription, CardFooter } from "components/ui/card";
import { Badge } from "components/ui/badge";
import { Avatar, AvatarFallback } from "components/ui/avatar";
import { AdCategoryBadge } from "../AdCategoryBadge";
import FavoriteAdButton from "components/Favorite/FavoriteAdButton";
import { VerifiedProfileBadge } from "components/provider/VerifiedProfileBadge";
import { cn } from "lib/utils";

export const AdCard: React.FC<{ ad: AdInterface; withFavoriteOverlay?: boolean }> = ({
    ad,
    withFavoriteOverlay = false,
}) => {
    const intl = useContext(IntlContext);

    const catKey = String(ad.category ?? "").toLowerCase();
    const clientInitials = `${ad?.client?.first_name?.charAt(0) ?? ""}${ad?.client?.last_name?.charAt(0) ?? ""}`;
    const relativeTime = moment(ad.created_at).locale("fr").fromNow();
    const isRecurring = ad.frequency === AdFrequency.RECURRING;
    const location = formatAdServiceLocation(ad);

    return (
        <Link
            to={buildRoute.ad(ad.id)}
            className="flex h-full"
            aria-label={String(intl.formatMessage("ad.card.aria_open_ad", { title: ad.title }))}
        >
            <Card variant="interactive" className="flex h-full w-full flex-col gap-4 rounded-3xl p-5">
                <div className={cn("flex items-start justify-between gap-3", withFavoriteOverlay && "pr-11")}>
                    <div className="flex min-w-0 items-center gap-3">
                        <Avatar>
                            {ad.client.picture_id && (
                                <Image id={ad.client.picture_id} className="aspect-square h-full w-full object-cover" />
                            )}
                            <AvatarFallback className="bg-secondary text-sm font-semibold">
                                {clientInitials || "TG"}
                            </AvatarFallback>
                        </Avatar>

                        <div className="min-w-0">
                            <div className="flex items-center gap-1.5">
                                <span className="truncate text-sm font-semibold leading-none">
                                    {ad.client.first_name} {ad.client.last_name?.charAt(0)}.
                                </span>
                                {ad.client?.dataleon_status === "approved" && (
                                    <VerifiedProfileBadge
                                        size="lg"
                                        labelKey="ad.card.verified_profile"
                                        className="align-text-bottom"
                                    />
                                )}
                            </div>
                            <div className="mt-1 flex items-center gap-1.5 text-xs text-muted-foreground">
                                <span>{relativeTime}</span>
                            </div>
                        </div>
                    </div>

                    <Badge variant="frequency" palette={isRecurring ? "violet" : "orange"} className="shrink-0">
                        {isRecurring ? <RefreshCw /> : <Timer />}
                        {isRecurring
                            ? intl.formatMessage("ad.card.frequency_recurring")
                            : intl.formatMessage("ad.card.frequency_one_time")}
                    </Badge>
                </div>

                <AdCategoryBadge category={catKey} label={catKey} />

                <CardTitle className="line-clamp-2 text-base font-bold leading-snug">{ad.title}</CardTitle>

                <div className="flex-1 overflow-hidden">
                    <CardDescription className="line-clamp-3 leading-relaxed">
                        {ad.description ?? intl.formatMessage("ad.card.no_description")}
                    </CardDescription>
                </div>

                <CardFooter className="flex items-center gap-1.5 p-0 text-xs text-muted-foreground mt-2">
                    <MapPin className="h-3.5 w-3.5 shrink-0" />
                    <span className="truncate">{location}</span>
                </CardFooter>
            </Card>
        </Link>
    );
};

export const AdListCard: React.FC<{ ad: AdInterface }> = ({ ad }) => (
    <div className="relative h-full">
        <div className="absolute right-3 top-3 z-10" onClick={(e) => e.stopPropagation()}>
            <FavoriteAdButton ad={ad} size="sm" />
        </div>
        <AdCard ad={ad} withFavoriteOverlay />
    </div>
);

export const AD_LIST_GRID_CLASS = "grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3";

type Props = {
    items: AdInterface[];
    gridClassName?: string;
};

const ComponentList: React.FC<Props> = ({ items, gridClassName = AD_LIST_GRID_CLASS }) => (
    <div className={gridClassName}>
        {items.map((ad) => (
            <AdListCard key={ad.id} ad={ad} />
        ))}
    </div>
);

export default ComponentList;
